/**
 * Phase 292: on-demand range narrative. One delegated `submit` listener on document handles
 * `form[data-narrative-range-form]`: POST, swap the server-rendered `snippet--narrativeRegion`
 * into `[data-narrative-region]`, re-run the glossary init on it, demote duplicate BODY triggers,
 * move focus. All copy is PHP-owned (data-busy-label, data-fail-line, JSON `message`).
 * Idempotent: the module-level flag survives every swap.
 *
 * 292 WR-03: the server renders the submit button `disabled`; this module enables it only once the
 * delegated listener is bound, so the form can never submit natively (the handler is ajax-only).
 */
import { Popover } from 'bootstrap';
import { initGlossary } from './glossary';

const SNIPPET_KEY = 'snippet--narrativeRegion';

// Body triggers only. Rules: (1) anchor triggers (legend/header, marked data-glossary-anchor
// server-side) are never selected, disposed or replaced; (2) only keys the swapped region claims
// as BODY triggers are touched; (3) the first body trigger per claimed key in DOM order is kept,
// later ones are demoted (this pass never promotes).
const BODY_TRIGGER = 'button.glossary-trigger[data-glossary-key]:not([data-glossary-anchor])';

let documentListenerBound = false;

export function demoteDuplicateBodyTriggers(region: Element): void {
    const claimed = new Set<string>();
    region.querySelectorAll<HTMLElement>(BODY_TRIGGER).forEach((b) => {
        const key = b.dataset.glossaryKey;
        if (key) {
            claimed.add(key);
        }
    });
    if (claimed.size === 0) {
        return;
    }

    const seen = new Set<string>();
    document.querySelectorAll<HTMLElement>(BODY_TRIGGER).forEach((btn) => {
        const key = btn.dataset.glossaryKey;
        if (!key || !claimed.has(key)) {
            return;
        }
        if (!seen.has(key)) {
            seen.add(key);
            return;
        }
        Popover.getInstance(btn)?.dispose();
        const rest = Array.from(btn.classList).filter((c) => c !== 'glossary-trigger');
        let replacement: Node;
        if (rest.length > 0) {
            const span = document.createElement('span');
            span.className = rest.join(' ');
            span.textContent = btn.textContent;
            replacement = span;
        } else {
            replacement = document.createTextNode(btn.textContent ?? '');
        }
        btn.replaceWith(replacement);
    });
}

/** Enables served-disabled range buttons under `root`; never touches one mid-request (aria-busy). */
export function enableRangeForms(root: ParentNode): void {
    root.querySelectorAll<HTMLButtonElement>(
        'form[data-narrative-range-form] [data-narrative-range-btn][disabled]:not([aria-busy])',
    ).forEach((btn) => {
        btn.disabled = false;
    });
}

function setBusy(form: HTMLFormElement, btn: HTMLButtonElement, busy: boolean, label: string): void {
    const spinner = form.querySelector<HTMLElement>('.spinner-border');
    btn.disabled = busy;
    btn.textContent = label;
    spinner?.classList.toggle('d-none', !busy);
    if (busy) {
        btn.setAttribute('aria-busy', 'true');
    } else {
        btn.removeAttribute('aria-busy');
    }
}

async function submitRange(form: HTMLFormElement, btn: HTMLButtonElement): Promise<void> {
    const original = btn.textContent ?? '';
    const msg = form.querySelector<HTMLElement>('[data-narrative-range-msg]');
    setBusy(form, btn, true, btn.dataset.busyLabel ?? original);
    if (msg) {
        msg.textContent = '';
    }

    let failure: string | null = null;
    try {
        const res = await fetch(form.action, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form),
            // Server-owned value (NarrativeRange::clientTimeoutMs(), 50 s); the fallback mirrors it.
            signal: AbortSignal.timeout(Number(form.dataset.timeoutMs) || 50000),
        });
        const json = await res.json();
        const html = json?.snippets?.[SNIPPET_KEY];
        if (res.ok && typeof html === 'string') {
            const region = form.closest<HTMLElement>('[data-narrative-region]');
            if (region) {
                region.innerHTML = html;
                enableRangeForms(region);
                initGlossary(region);
                demoteDuplicateBodyTriggers(region);
                let target = region.querySelector<HTMLElement>('[data-narrative-kind="range"]');
                if (!target) {
                    target = region;
                    target.setAttribute('tabindex', '-1');
                }
                target.focus();
                return;
            }
        }
        failure = json && json.ok === false && typeof json.message === 'string' ? json.message : null;
    } catch {
        failure = null;
    }

    setBusy(form, btn, false, original);
    if (msg) {
        msg.textContent = failure ?? form.dataset.failLine ?? '';
    }
    btn.focus();
}

export function initNarrativeRange(): void {
    if (documentListenerBound) {
        return;
    }
    documentListenerBound = true;

    document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('form[data-narrative-range-form]')) {
            return;
        }
        event.preventDefault();
        const btn = form.querySelector<HTMLButtonElement>('[data-narrative-range-btn]');
        if (!btn || btn.disabled) {
            return;
        }
        void submitRange(form, btn);
    });

    // Only after the listener above is bound (WR-03).
    enableRangeForms(document);
}

export default class {
    static init = () => {
        initNarrativeRange();
    };
}
